import { Highlight } from '@curology/ui-components-web/react/Highlight';
import { Label } from '@curology/ui-components-web/react/Label';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React from 'react';

import { type Card } from '@/types/productSelectionAndCheckout/types';

import { IngredientsAccordion } from '../IngredientsAccordion';
import { ModalImage } from '../ModalImage';
import { ModalPricingVolumeAndProductOptionSelector } from '../ModalPricingVolumeAndProductOptionSelector';

export const FutureProofModalContent = ({ modalCard }: { modalCard: Card }) => {
  const { modal } = modalCard;

  return (
    <>
      <ModalImage modal={modal} />
      <Typography className="cur-mt-4" size="h2">
        Future-Proof<sup>Rx</sup>
      </Typography>
      <Typography className="cur-mt-4" weight="bold">
        Daily Preventative Anti-Aging Treatment
      </Typography>
      <ModalPricingVolumeAndProductOptionSelector
        className="cur-mt-1"
        modalCard={modalCard}
      />
      <Label className="cur-mt-2" labelType="subtle">
        Customized
      </Label>
      {modalCard.recommendationText && (
        <Highlight className="cur-mt-4" state="success">
          <div
            dangerouslySetInnerHTML={{
              __html: modalCard.recommendationText.modalCallout,
            }}
          ></div>
        </Highlight>
      )}
      <Typography className="cur-mt-4" size="h3">
        Why it&apos;s special
      </Typography>
      <Typography className="cur-mt-4">
        Stop the cycle of trying, buying, and DIYing—help prevent the signs of
        aging with a more effective treatment made specifically for your skin:
        Curology Future-Proof<sup>Rx</sup>
      </Typography>
      <Typography className="cur-mt-4">
        Customized by a dermatology provider who selects prescription-strength
        ingredients like tretinoin (clinically proven to help prevent early
        signs of aging), Curology Future-ProofRx uses your skin&apos;s unique
        history and future goals to streamline your routine. Whether that&apos;s
        refining the texture and tone of your skin or defending against wrinkles
        and dark spots—all while helping to keep your skin in the clear.
        Don&apos;t wait for fate—reach for Curology Future-Proof<sup>Rx</sup>{' '}
        and face the future with (glowing) confidence.
      </Typography>
      <Typography className="cur-mt-4">
        Your provider will review your answers and photos, and pick a few of the
        following active ingredients for your skins unique needs.
      </Typography>
      <Typography className="cur-mt-4" size="h3">
        How to use it
      </Typography>
      <Typography className="cur-mt-4">
        After cleansing, apply a thin layer of Curology Future-Proof
        <sup>Rx</sup> to your entire face and neck before bed. Leave on
        overnight.
      </Typography>
      <Typography className="cur-mt-4" size="h3">
        Active ingredients
      </Typography>
      <IngredientsAccordion
        body="Gently exfoliates and reduces excess pigment to help smooth and
        brighten skin"
        title="Azelaic acid"
      />
      <IngredientsAccordion
        body="Attracts and holds moisture for softer, smoother skin"
        title="Dexpanthenol"
      />
      <IngredientsAccordion
        body="Fights free radicals, fades dark spots, and improves moisture
        barrier function"
        title="Niacinamide"
      />
      <IngredientsAccordion
        body="Helps lighten dark spots and reduce redness"
        title="Tranexamic acid"
      />
      <IngredientsAccordion
        body="Helps smooth texture and fine lines while brightening skin"
        title="Tretinoin"
      />
      <Typography className="cur-mt-4" size="h3">
        Inactive ingredients
      </Typography>
      <Typography className="cur-mt-4">
        Aloe Barbadensis Leaf Juice, C13-C14 Isoparaffin, Caprylic/Capric
        Triglyceride, Deionized Water, Laureth-7, Phenoxyethanol,
        Polyacrylamide, Tocopheryl Acetate, Triethylene Glycol
      </Typography>
      <Typography className="cur-mt-4">
        All formulas containing tretinoin also contain the antioxidant butylated
        hydroxytoluene (BHT).
      </Typography>
      <Typography className="cur-mt-4">
        Formulas containing both niacinamide and azelaic acid may also contain
        propylene glycol.
      </Typography>
    </>
  );
};
